'use client';

import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  Select as SelectUI,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

type SelectProps = Omit<React.ComponentProps<typeof SelectUI>, 'onChange'> & {
  className?: string;
  label?: string;
  placeholder?: string;
  options: {
    label: string;
    value: string;
    color?: string;
  }[];
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: string;
  description?: string;
};

export default function Select({
  className,
  label,
  placeholder,
  options,
  onChange,
  disabled,
  error,
  description,
  ...props
}: SelectProps) {
  return (
    <div className='flex flex-col items-start gap-3 self-stretch'>
      {label && (
        <label className='font-satoshi text-base font-medium capitalize text-[#1F2A37] dark:text-white'>
          {label}
        </label>
      )}
      <SelectUI disabled={disabled} {...props}>
        <SelectTrigger
          className={cn(
            'h-[54px] w-full',
            'p-4',
            'rounded-lg bg-white text-[#384250]',
            'border-2 border-black/15',
            'hover:bg-black/5',
            'focus:border-[#7846FF] focus:outline-none',
            'ring-0 focus:ring-1 focus:ring-[#7846FF]',
            'dark:border-gray-700 dark:bg-gray-800 dark:text-white',
            'dark:hover:bg-gray-700',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            disabled &&
              'cursor-not-allowed opacity-50 hover:bg-white dark:hover:bg-gray-800',
            className
          )}
          aria-invalid={!!error}
          aria-describedby={
            error ? 'error-message' : description ? 'description' : undefined
          }
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className='border-black/15 bg-white dark:border-gray-700 dark:bg-gray-800'>
          <SelectGroup>
            <SelectLabel className='text-[#1F2A37] dark:text-white'>
              {label}
            </SelectLabel>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className='text-[#384250] hover:bg-black/5 dark:text-white dark:hover:bg-gray-700'
              >
                <div className='flex items-center gap-2'>
                  {option.color && (
                    <div
                      className='h-3 w-3 rounded-full'
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  {option.label}
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </SelectUI>
      {description && !error && (
        <p
          id='description'
          className='text-sm text-gray-500 dark:text-gray-400'
        >
          {description}
        </p>
      )}
      {error && (
        <p id='error-message' className='text-sm text-red-500'>
          {error}
        </p>
      )}
    </div>
  );
}
